import { useRef, useState } from 'react';
import { fmtMonthDay, type ProgramCharter, type ProgramDTO } from '@wd/shared';
import { errorText } from '../../lib/api';
import { useMe, useMembers, useProgramActions } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { Avatar, Button } from '../../ui/primitives';
import { Editable } from './Editable';

// 项目说明：PRINCE2 项目启动文档（PID）的精简版。写清目标、范围、交付物和容差，每期汇报都对照它。

const FIELDS: { key: keyof ProgramCharter; label: string; hint: string }[] = [
  { key: 'goal', label: '背景与目标', hint: '为什么做、做成什么样算成功，例如：25kW 混合逆变器 2026 年 12 月前拿到意大利、波兰认证并量产' },
  { key: 'scope', label: '范围：做什么', hint: '包含的产品、功能、认证、市场' },
  { key: 'outOfScope', label: '范围：不做什么', hint: '明确不在这次范围里的事，避免需求不断加进来' },
  { key: 'deliverables', label: '交付物', hint: '每条一行，例如：定版软件、认证证书、量产 BOM、作业指导书' },
  { key: 'constraints', label: '约束与容差', hint: '期限、预算、资源上的约束，以及允许的偏差。例如：关键任务延后超过 2 周要上报决策人' },
];

export function CharterTab({ program }: { program: ProgramDTO }) {
  const { update } = useProgramActions(program.id);
  const [err, setErr] = useState<string | null>(null);
  const can = program.canEdit;
  const save = (patch: Parameters<typeof update.mutate>[0]) => update.mutate(patch, { onError: (e) => setErr(errorText(e)) });

  return (
    <div className="pg-charter">
      <div className="pg-charter-main">
        {FIELDS.map((f) => (
          <section key={f.key} className="pg-sec">
            <div className="pg-sec-head">{f.label}</div>
            <Editable
              value={program.charter[f.key]}
              canEdit={can}
              multiline
              placeholder={f.hint}
              maxLength={2000}
              className="pg-block"
              onSave={(v) => save({ charter: { [f.key]: v } })}
            />
          </section>
        ))}
        {err && <div className="t-meta c-red">{err}</div>}
      </div>
      <aside className="pg-charter-side">
        <People program={program} />
        <div className="pg-kv">
          <span className="k">起止</span>
          {can ? (
            <span className="row" style={{ gap: 6 }}>
              <input className="wd-input" type="date" value={program.startDate ?? ''} onChange={(e) => save({ startDate: e.target.value || null })} aria-label="开始" />
              <input className="wd-input" type="date" value={program.endDate ?? ''} onChange={(e) => save({ endDate: e.target.value || null })} aria-label="计划结束" />
            </span>
          ) : (
            <span>
              {program.startDate ? fmtMonthDay(program.startDate) : '?'} – {program.endDate ? fmtMonthDay(program.endDate) : '?'}
            </span>
          )}
        </div>
        {can && (
          <div className="pg-kv">
            <span className="k">状态</span>
            <Button variant="text" onClick={() => save({ status: program.status === 'active' ? 'closed' : 'active' })}>
              {program.status === 'active' ? '关闭项目集' : '重新打开'}
            </Button>
          </div>
        )}
        <div className="pg-note">参考 PRINCE2 的项目启动文档（PID）：先把目标、范围和容差说清楚，之后每期汇报都对照计划基线写进展和偏差。</div>
      </aside>
    </div>
  );
}

function People({ program }: { program: ProgramDTO }) {
  const { update } = useProgramActions(program.id);
  const { data: members = [] } = useMembers();
  const { data: me } = useMe();
  const [picking, setPicking] = useState(false);
  const addRef = useRef<HTMLButtonElement>(null);
  const can = program.canEdit;
  const people = members.filter((m) => m.status === 'active' || m.status === 'invited');
  const to = new Set(program.reportTo.map((x) => x.id));
  const canChangePm = can && (me?.role === 'admin' || me?.role === 'manager' || me?.id === program.pm.id || me?.id === program.sponsor?.id);
  return (
    <>
      <div className="pg-kv">
        <span className="k">项目经理</span>
        {canChangePm ? (
          <select className="pf-select" value={program.pm.id} onChange={(e) => update.mutate({ pmId: e.target.value })}>
            {people.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        ) : (
          <span>{program.pm.name}</span>
        )}
      </div>
      <div className="pg-kv">
        <span className="k">决策人</span>
        {can ? (
          <select className="pf-select" value={program.sponsor?.id ?? ''} onChange={(e) => update.mutate({ sponsorId: e.target.value || null })}>
            <option value="">不设</option>
            {people.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        ) : (
          <span>{program.sponsor?.name ?? '—'}</span>
        )}
      </div>
      <div className="pg-kv col">
        <span className="k">汇报发给</span>
        <div className="pf-people">
          {program.reportTo.map((x) => (
            <span key={x.id} className="pf-chip">
              <Avatar initial={x.initial} seed={x.name} size={16} />
              {x.name}
              {can && (
                <button type="button" aria-label={`移除${x.name}`} onClick={() => update.mutate({ reportToIds: [...to].filter((id) => id !== x.id) })}>
                  <Icon name="close" size={12} />
                </button>
              )}
            </span>
          ))}
          {can && (
            <button ref={addRef} type="button" className="pf-add" onClick={() => setPicking(true)}>
              <Icon name="plus" size={14} />
              添加
            </button>
          )}
          {!program.reportTo.length && !can && <span className="c4">—</span>}
        </div>
        <Popover anchor={addRef} open={picking} onClose={() => setPicking(false)} width={260}>
          <MenuList
            search
            items={people.filter((m) => m.id !== program.pm.id).map((m) => ({ value: m.id, label: m.name, icon: <Avatar initial={m.initial} seed={m.name} />, hint: m.teamName ?? '' }))}
            selected={to}
            onPick={(id) => {
              const n = new Set(to);
              if (n.has(id)) n.delete(id);
              else n.add(id);
              update.mutate({ reportToIds: [...n] });
            }}
          />
        </Popover>
        <span className="t-meta">决策人会自动收到汇报</span>
      </div>
    </>
  );
}
